import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { DialogConfig, DialogService } from "@obg/mat.ui.core";
import {
	CORE_ROUTE,
	CoreState,
	CovidMeasuresConfigService,
	DialogStatusSelector,
	ResponsibleGamingConfigService,
	SessionLimitRequiredService,
} from "@obg/ngx.core";
import { DeviceConfigService } from "@obg/ngx.global";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { combineLatest, Observable, of } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { SgaSessionLimitDialogContainer } from "./session-limit-dialog/sga-session-limit-dialog.container";

@Injectable({
	providedIn: "root",
})
export class SgaSessionLimitRequiredGuard implements CanActivate {
	mobileDialogConfig: Partial<DialogConfig> = {
		panelClass: ["obg-m-sga-session-limit-dialog", "obg-m-alert-extra-wide"],
		closeOnNavigation: true,
		disableClose: true,
		width: "100%",
		height: "100%",
		maxWidth: "none",
		maxHeight: "none",
	};

	constructor(
		private responsibleGamingConfig: ResponsibleGamingConfigService,
		private sessionLimitRequiredService: SessionLimitRequiredService,
		private dialogService: DialogService,
		private dialogSelector: DialogStatusSelector,
		private store: Store<CoreState>,
		private covidMeasuresConfig: CovidMeasuresConfigService,
		private deviceConfigService: DeviceConfigService
	) {}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		if (
			!this.covidMeasuresConfig.covidMeasuresEnabled() ||
			!this.responsibleGamingConfig.getSessionLimitIsRequired()
		) {
			return of(true);
		}

		return this.sessionLimitRequiredService.shouldOpenSessionLimit$().pipe(
			switchMap((shouldRedirect) =>
				!shouldRedirect
					? of(true)
					: combineLatest([
							this.store.select(RouteSelector.getCurrent),
							this.store.pipe(select(this.dialogSelector.hasOpenDialogs())),
						]).pipe(
							filter(([, hasOpenDialogs]) => !hasOpenDialogs),
							tap(([currentRoute]) => {
								const fallbackLobby = route.data?.product || CORE_ROUTE.home;
								if (this.deviceConfigService.isDesktop()) {
									this.store.dispatch(
										RouteAction.navigate(
											!currentRoute
												? fallbackLobby
												: CORE_ROUTE.responsibleGaming.sessionLimitDialog,
											undefined,
											{ queryParams: { returnUrl: state.url } }
										)
									);
									return;
								}

								this.dialogService
									.openComponent(SgaSessionLimitDialogContainer, this.mobileDialogConfig)
									.beforeClosed()
									.pipe(
										switchMap(() => this.sessionLimitRequiredService.isSessionLimitUnset$()),
										tap((isUnset) => {
											if (!isUnset) {
												this.store.dispatch(RouteAction.navigateByUrl(state.url));
												return;
											}

											if (currentRoute?.name === CORE_ROUTE.auth.login) {
												this.store.dispatch(RouteAction.navigate(fallbackLobby));
											}
										}),
										take(1)
									)
									.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription
							}),
							map(() => false),
							take(1)
						)
			)
		);
	}
}
